import { Quaternion, Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import { headRotation, headWorldPosition } from '../graphics/robotRig';
import { clamp, degToRad, quaternionFromBasis, rightFromForwardUp } from '../utils/math';
import type { Robot } from '../player/Robot';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { CameraTransform } from './CameraTransform';

const scratchForward = new Vector3();
const scratchRight = new Vector3();
const scratchUp = new Vector3();
const scratchBack = new Vector3();
const scratchRolled = new Vector3();
const scratchHeadQuaternion = new Quaternion();
const scratchEye = new Vector3();

/**
 * The combat camera: mounted on the robot's head so the player aims down the
 * barrel. The body may face elsewhere while the head tracks a target, which is
 * exactly what a quadruped can do.
 */
export class FirstPersonCamera {
  /** Head yaw relative to the body heading, radians. */
  headYaw = 0;
  /** Head pitch, radians (positive looks up). */
  headPitch = 0;
  /** Head roll relative to the body, radians. */
  headRoll = 0;
  /** Head-tracking viewer offset in camera-local metres (x right, y up, z forward). */
  readonly viewOffset = new Vector3();

  constructor(private readonly collision?: CollisionWorld) {}

  update(
    robot: Robot,
    baseForward: Vector3,
    out: CameraTransform,
    horizonLock: boolean,
    /** The body's up as the camera sees it; damped while the body turns. */
    bodyUp?: Vector3,
    /**
     * The plane the look axes are built in. Omitted, the body's own up, which
     * is the shipped behaviour; the rig passes the pinned plane when the view
     * is being held on the surface the player stepped off.
     */
    planeUp?: Vector3,
  ): void {
    const settings = BALANCE.camera.firstPerson;
    const up = planeUp ?? robot.up;

    // Base view direction: body forward rotated by the head yaw around the up.
    rightFromForwardUp(baseForward, up, scratchRight);
    const cosYaw = Math.cos(this.headYaw);
    const sinYaw = Math.sin(this.headYaw);
    scratchForward
      .copy(baseForward)
      .multiplyScalar(cosYaw)
      .addScaledVector(scratchRight, sinYaw)
      .normalize();

    // Pitch the head around its own right axis.
    rightFromForwardUp(scratchForward, up, scratchRight);
    const cosPitch = Math.cos(this.headPitch);
    const sinPitch = Math.sin(this.headPitch);
    scratchForward
      .copy(scratchForward)
      .multiplyScalar(cosPitch)
      .addScaledVector(up, sinPitch)
      .normalize();
    scratchUp.copy(scratchRight).cross(scratchForward).normalize();

    if (bodyUp) {
      // Damp the roll so a fast mid-air turn does not spin the world around
      // the player. The view still shifts with the body, just less violently.
      scratchUp.lerp(bodyUp, BALANCE.camera.rollDamping);
    }

    if (horizonLock) {
      // Cancel most of the roll so the horizon stays level, in whichever plane
      // the view is being held.
      scratchUp.lerp(up, 0.65);
    }

    // Re-orthonormalise after the roll damping. Without this the up vector can
    // tilt off the forward plane, which breaks the camera direction at the
    // poles where the pod can now look straight up or down.
    scratchUp.addScaledVector(scratchForward, -scratchUp.dot(scratchForward));
    if (scratchUp.lengthSq() < 1e-8) {
      scratchUp.set(0, 0, 1).addScaledVector(scratchForward, -scratchForward.z);
      if (scratchUp.lengthSq() < 1e-8) {
        scratchUp.set(0, 1, 0).addScaledVector(scratchForward, -scratchForward.y);
      }
    }
    scratchUp.normalize();
    rightFromForwardUp(scratchForward, scratchUp, scratchRight);
    scratchUp.crossVectors(scratchRight, scratchForward).normalize();

    if (this.headRoll !== 0) {
      const roll = Math.cos(this.headRoll);
      const sin = Math.sin(this.headRoll);
      scratchRolled.copy(scratchRight).multiplyScalar(roll).addScaledVector(scratchUp, sin);
      scratchUp
        .copy(scratchUp)
        .multiplyScalar(roll)
        .addScaledVector(scratchRight, -sin)
        .normalize();
      scratchRight.copy(scratchRolled).normalize();
    }

    scratchBack.copy(scratchForward).negate();
    quaternionFromBasis(scratchRight, scratchUp, scratchBack, out.quaternion);

    // The eye is mounted on the head, not the body. The gun is on the head
    // too, so this keeps it fixed relative to the view instead of sweeping
    // across the centre when the neck turns.
    const [offsetX, offsetY, offsetZ] = settings.eyeOffset;
    headRotation(this.headYaw, this.headPitch, this.headRoll, scratchHeadQuaternion);
    scratchEye.set(offsetX, offsetY, offsetZ).applyQuaternion(scratchHeadQuaternion);
    // Match the visual neck stretch in RobotView so the eye stays on the head.
    scratchEye.y += Math.abs(this.headPitch) * 0.06;
    scratchEye.applyQuaternion(robot.bodyQuaternion);
    headWorldPosition(robot.position, robot.bodyQuaternion, out.position).add(scratchEye);
    // The head-tracking viewer offset is a camera-local translation: right,
    // up, then forward. It is what the off-axis projection uses.
    out.position
      .addScaledVector(scratchRight, this.viewOffset.x)
      .addScaledVector(scratchUp, this.viewOffset.y)
      .addScaledVector(scratchForward, this.viewOffset.z);

    // If a wall is closer than the comfortable head clearance, pull the eye
    // back along the view direction instead of letting the near plane clip.
    if (this.collision) {
      const hit = this.collision.raycast(out.position, scratchForward, settings.pullInDistance);
      if (hit) {
        out.position.addScaledVector(scratchForward, hit.distance - settings.pullInDistance);
      }
    }
  }

  clampPitch(): void {
    const settings = BALANCE.camera.firstPerson;
    this.headPitch = clamp(
      this.headPitch,
      degToRad(settings.minPitch),
      degToRad(settings.maxPitch),
    );
  }
}

/** Quaternion type re-exported for convenience. */
export type { Quaternion };
